import type { WriteIdentity } from '@/core/api/http'
import { apiGetOneOrNull, apiListAll, apiPatch, apiPut } from '@/core/api/resources'
import type {
  BoardCoaching,
  CoachOption,
  BoardDokument,
  CoachingCreate,
  CoachingGueltigkeit,
  CoachingVertragRef,
  CoachingVoucherRef,
} from '@/core/data/coachings'
import type { CoachingAzavPatch } from '../api/resources'

export async function listBoardCoachings(
  organisationId: string,
  onFirstPageHeaders?: (headers: Headers) => void,
): Promise<BoardCoaching[]> {
  const rows = await apiListAll('/azav/coaching', {
    organisation_id: organisationId,
    include: ['massnahme', 'members', 'agentur', 'teilnahme'],
  }, undefined, onFirstPageHeaders)
  const assignments = rows.some((row) => row.second_coach_id)
    ? await apiListAll('/mitgliedschaft', { organisation_id: organisationId, is_coach: true, include: ['members'] })
    : []
  const members = new Map(assignments.flatMap((membership) => membership.members ? [[membership.members.id, membership.members] as const] : []))
  return rows.map((c) => ({
    id: c.id,
    secondCoach: c.second_coach_id ? members.get(c.second_coach_id) ?? null : null,
    members: c.members,
    agentur: c.agentur,
    teilnahme: c.teilnahme,
    budget: {
      zeitl_umf_max_ue: c.zeitl_umf_max_ue,
      gueltigkeit_beginn: c.gueltigkeit_beginn,
      gueltigkeit_ende: c.gueltigkeit_ende,
      massnahme: c.massnahme ? { ue_anzahl: c.massnahme.ue_anzahl } : null,
    },
  }))
}

export async function listBoardDokumente(coachingId: string): Promise<BoardDokument[]> {
  const rows = await apiListAll('/azav/dokumente', {
    coaching_id: coachingId,
    include: ['sitzung'],
  })
  return rows.map((d) => ({
    name: d.name,
    sitzung: d.sitzung ?? null,
    sheet: false,
  }))
}

export async function getGueltigkeit(coachingId: string): Promise<CoachingGueltigkeit | null> {
  const row = await apiGetOneOrNull('/azav/coaching/{id}', { id: coachingId })
  return row ? { beginn: row.gueltigkeit_beginn, ende: row.gueltigkeit_ende } : null
}

export async function createCoaching(row: CoachingCreate): Promise<void> {
  await apiPut('/azav/coaching', {
    ...row,
    avgs_nummer: 0,
    inhalt_gruppen: '',
    inhalt_einzel: '',
    max_dauer: '',
    gueltigkeit_beginn: '1970-01-01',
    gueltigkeit_ende: '1970-01-01',
    zeitl_umf_kategorie: '',
    zeitl_umf_max_ue: 0,
  })
}

export async function patchCoaching(coachingId: string, patch: CoachingAzavPatch, identity?: WriteIdentity): Promise<void> {
  await apiPatch('/azav/coaching/{id}', { id: coachingId }, patch, identity)
}

export async function getCoachingVertragRef(coachingId: string): Promise<CoachingVertragRef | null> {
  const row = await apiGetOneOrNull('/azav/coaching/{id}', { id: coachingId }, {
    include: ['agentur', 'massnahme'],
  })
  if (!row) return null
  const massnahme = row.massnahme
    ? await apiGetOneOrNull('/azav/massnahme/{id}', { id: row.massnahme.id })
    : null
  return {
    avgs_nummer: row.avgs_nummer,
    zeitl_umf_max_ue: row.zeitl_umf_max_ue,
    agentur: row.agentur
      ? { behoerde: row.agentur.behoerde ?? '', standort: row.agentur.standort ?? '' }
      : null,
    massnahme: massnahme
      ? {
          nummer: massnahme.nummer,
          bezeichnung: massnahme.bezeichnung,
          ue_anzahl: massnahme.ue_anzahl,
          kosten: massnahme.kosten,
          abschluss: massnahme.abschluss,
        }
      : null,
  }
}

export async function getCoachingVoucherRef(coachingId: string): Promise<CoachingVoucherRef | null> {
  const row = await apiGetOneOrNull('/azav/coaching/{id}', { id: coachingId }, {
    include: ['massnahme'],
  })
  if (!row) return null
  return {
    inhalt_gruppen: row.inhalt_gruppen,
    inhalt_einzel: row.inhalt_einzel,
    zeitl_umf_kategorie: row.zeitl_umf_kategorie,
    zeitl_umf_max_ue: row.zeitl_umf_max_ue,
    gueltigkeit_beginn: row.gueltigkeit_beginn,
    gueltigkeit_ende: row.gueltigkeit_ende,
    max_dauer: row.max_dauer,
    massnahme: row.massnahme ? { nummer: row.massnahme.nummer } : null,
  }
}

export async function getAssignedCoaches(coachingId: string): Promise<CoachOption[]> {
  const row = await apiGetOneOrNull('/azav/coaching/{id}', { id: coachingId }, { include: ['members'] })
  if (!row) return []
  const second = row.second_coach_id
    ? await apiGetOneOrNull('/members/{id}', { id: row.second_coach_id })
    : null
  return [row.members, second].flatMap((member) => member ? [{
    id: member.id,
    name: [member.vorname, member.nachname].filter(Boolean).join(' ').trim(),
  }] : [])
}

/** How the Trägerbestätigung reaches the Kostenträger (ADM-82): `digital`,
 *  Admination fills the uploaded blank; `original`, the customer submits the
 *  paper original and only records its data here. An unchosen (NULL) column
 *  is the digital default. */
export type TbEinreichung = 'digital' | 'original'

/** The submission choice and the voucher's Kostenträger, or null when the
 *  coaching is not visible. */
export async function getTbEinreichung(coachingId: string): Promise<{
  einreichung: TbEinreichung
  agentur: { behoerde: string; standort: string } | null
} | null> {
  const row = await apiGetOneOrNull('/azav/coaching/{id}', { id: coachingId }, { include: ['agentur'] })
  if (!row) return null
  return {
    einreichung: row.tb_einreichung === 'original' ? 'original' : 'digital',
    agentur: row.agentur ? { behoerde: row.agentur.behoerde ?? '', standort: row.agentur.standort ?? '' } : null,
  }
}

export async function setTbEinreichung(coachingId: string, einreichung: TbEinreichung, identity?: WriteIdentity): Promise<void> {
  await patchCoaching(coachingId, { tb_einreichung: einreichung }, identity)
}
